<script setup lang="ts">
  import { computed, ref } from "vue";
  import { storeToRefs } from "pinia";
  import draggable from "vuedraggable";
  import { GripVertical, ChevronDown } from "lucide-vue-next";
  import { DateTimePicker } from "@/components/episto-ui/date-time-picker";
  import { useCampaignsPivotStore } from "@stores/useCampaignsPivotStore";
  import { dimensionLabel, findDimension, type DimensionPick } from "../pivotDimensions";
  import type { PivotFilterOption } from "@/types";
  import DimensionThumbnail from "./DimensionThumbnail.vue";
  import PivotScopeSelectors from "./PivotScopeSelectors.vue";
  import { PIVOT_PERIOD_PRESETS, activePresetKey, type PivotPeriodPreset } from "../pivotPeriods";

  const store = useCampaignsPivotStore();
  const {
    selectedSurveys,
    rowDimensions,
    columnDimensions,
    rowFilters,
    filterOptions,
    dateFrom,
    dateTo,
  } = storeToRefs(store);

  // ── Filters ───────────────────────────────────────────────────────────────

  const toDateOnly = (value: unknown) => (typeof value === "string" && value ? value.slice(0, 10) : null);

  const activePreset = computed(() => activePresetKey({ from: dateFrom.value, to: dateTo.value }));

  function applyPreset(preset: PivotPeriodPreset) {
    const range = preset.range(new Date());
    dateFrom.value = range.from;
    dateTo.value = range.to;
  }

  const expandedDims = ref<Record<string, boolean>>({});
  const filterSearch = ref<Record<string, string>>({});

  const filterKeyOf = (dimValue: string) => findDimension(dimValue)?.filterKey ?? null;

  // Campaign statuses are a fixed pair: offering them before any scope lets the
  // status filter act as the scope ("every live campaign, across accounts").
  const STATUS_OPTIONS: PivotFilterOption[] = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];

  function getOptions(dimValue: string): PivotFilterOption[] {
    if (dimValue === "survey" && selectedSurveys.value.length) {
      return selectedSurveys.value.map((s) => ({ value: String(s.id), label: s.name || String(s.id) }));
    }
    if (dimValue === "m_status") return STATUS_OPTIONS;
    const key = filterKeyOf(dimValue);
    return key ? filterOptions.value[key] || [] : [];
  }

  function getFilteredOptions(dimValue: string) {
    const opts = getOptions(dimValue);
    const q = (filterSearch.value[dimValue] || "").toLowerCase();
    return q ? opts.filter((o) => String(o.label ?? o.value).toLowerCase().includes(q)) : opts;
  }

  function selectedValues(dimValue: string): string[] {
    const key = filterKeyOf(dimValue);
    return key ? rowFilters.value[key] || [] : [];
  }

  const filterCount = (dimValue: string) => selectedValues(dimValue).length;
  const isSelected = (dimValue: string, optValue: string) => selectedValues(dimValue).includes(String(optValue));
  const isFilterable = (dimValue: string) => getOptions(dimValue).length > 0 || filterCount(dimValue) > 0;
  const hasSearch = (dimValue: string) => getOptions(dimValue).length > 6;

  function toggleFilter(dimValue: string, optValue: string) {
    const key = filterKeyOf(dimValue);
    if (!key) return;
    const current = selectedValues(dimValue).map(String);
    const val = String(optValue);
    rowFilters.value = {
      ...rowFilters.value,
      [key]: current.includes(val) ? current.filter((v) => v !== val) : [...current, val],
    };
  }

  // With a search typed, one click selects (or clears) every match instead of ticking rows one by one.
  function setMatches(dimValue: string, selected: boolean) {
    const key = filterKeyOf(dimValue);
    if (!key) return;
    const matches = getFilteredOptions(dimValue).map((o) => String(o.value));
    const current = new Set(selectedValues(dimValue).map(String));
    if (selected) matches.forEach((v) => current.add(v));
    else matches.forEach((v) => current.delete(v));
    rowFilters.value = { ...rowFilters.value, [key]: [...current] };
  }

  const allMatchesSelected = (dimValue: string) => {
    const matches = getFilteredOptions(dimValue);
    const current = new Set(selectedValues(dimValue).map(String));
    return matches.length > 0 && matches.every((o) => current.has(String(o.value)));
  };

  function toggleExpand(dimValue: string) {
    expandedDims.value = { ...expandedDims.value, [dimValue]: !expandedDims.value[dimValue] };
  }

  const onRowChecked = (dim: DimensionPick, event: Event) =>
    store.setDimensionChecked("row", dim.value, (event.target as HTMLInputElement).checked);
  const onColumnChecked = (dim: DimensionPick, event: Event) =>
    store.setDimensionChecked("column", dim.value, (event.target as HTMLInputElement).checked);
</script>

<template>
  <div class="flex flex-col rounded-xl border bg-card overflow-y-auto max-h-[calc(100vh-40px)]">
    <div class="p-4 flex flex-col gap-3">
      <PivotScopeSelectors />

      <div class="border-t" />

      <div class="flex flex-col gap-1.5">
        <p class="text-sm font-medium">Period</p>
        <div class="grid grid-cols-2 gap-2">
          <DateTimePicker
            :model-value="dateFrom"
            placeholder="Start"
            @update:model-value="dateFrom = toDateOnly($event)" />
          <DateTimePicker
            :model-value="dateTo"
            placeholder="End"
            @update:model-value="dateTo = toDateOnly($event)" />
        </div>
        <div class="flex flex-wrap gap-1 pt-0.5">
          <button
            v-for="preset in PIVOT_PERIOD_PRESETS"
            :key="preset.key"
            type="button"
            class="rounded-full border px-2 py-0.5 text-2xs leading-4 transition-colors"
            :class="
              activePreset === preset.key
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border text-muted-foreground hover:bg-muted/60 hover:text-foreground'
            "
            @click="applyPreset(preset)">
            {{ preset.label }}
          </button>
        </div>
      </div>

      <div class="border-t" />

      <div class="flex flex-col gap-0.5">
        <p class="text-sm font-medium mb-1">Row dimensions</p>

        <draggable
          v-model="rowDimensions"
          item-key="value"
          handle=".drag-handle"
          ghost-class="opacity-40"
          class="flex flex-col gap-0">
          <template #item="{ element: dim }">
            <div class="flex flex-col">
              <div class="flex items-center gap-1.5 rounded px-1 py-1 hover:bg-muted/50">
                <GripVertical
                  class="drag-handle size-3.5 text-muted-foreground/40 shrink-0 cursor-grab active:cursor-grabbing" />
                <input
                  :id="`row-dim-${dim.value}`"
                  :checked="dim.checked"
                  type="checkbox"
                  class="accent-primary"
                  @change="onRowChecked(dim, $event)" />
                <label
                  :for="`row-dim-${dim.value}`"
                  class="flex-1 text-sm cursor-pointer select-none"
                  :class="dim.checked ? 'text-foreground' : 'text-muted-foreground'">
                  {{ dimensionLabel(dim.value) }}
                </label>

                <template v-if="isFilterable(dim.value)">
                  <span
                    v-if="filterCount(dim.value) > 0"
                    class="text-xs bg-primary text-primary-foreground rounded-full px-1.5 py-0 leading-5 min-w-[20px] text-center">
                    {{ filterCount(dim.value) }}
                  </span>
                  <button
                    class="size-5 flex items-center justify-center rounded text-muted-foreground hover:text-foreground transition-colors"
                    @click.stop="toggleExpand(dim.value)">
                    <ChevronDown
                      class="size-3.5 transition-transform"
                      :class="expandedDims[dim.value] ? 'rotate-180' : ''" />
                  </button>
                </template>
              </div>

              <div
                v-if="isFilterable(dim.value) && expandedDims[dim.value]"
                class="ml-7 mb-1 flex flex-col gap-1">
                <input
                  v-if="hasSearch(dim.value)"
                  :value="filterSearch[dim.value] || ''"
                  type="text"
                  placeholder="Search..."
                  class="h-6 w-full rounded border border-input bg-background px-2 text-xs outline-none focus:ring-1 focus:ring-ring"
                  @input="filterSearch = { ...filterSearch, [dim.value]: ($event.target as HTMLInputElement).value }" />
                <div
                  v-if="hasSearch(dim.value) && getFilteredOptions(dim.value).length"
                  class="flex items-center justify-between px-1 text-2xs text-muted-foreground">
                  <span>{{ getFilteredOptions(dim.value).length }} match{{ getFilteredOptions(dim.value).length > 1 ? "es" : "" }}</span>
                  <button
                    type="button"
                    class="hover:text-foreground"
                    @click="setMatches(dim.value, !allMatchesSelected(dim.value))">
                    {{ allMatchesSelected(dim.value) ? "Clear matches" : "Select all matches" }}
                  </button>
                </div>

                <div class="max-h-36 overflow-y-auto flex flex-col gap-0.5">
                  <label
                    v-for="opt in getFilteredOptions(dim.value)"
                    :key="opt.value"
                    class="flex items-center gap-1.5 px-1 py-0.5 rounded text-xs cursor-pointer hover:bg-muted/50 select-none">
                    <input
                      type="checkbox"
                      :checked="isSelected(dim.value, opt.value)"
                      class="accent-primary"
                      @change="toggleFilter(dim.value, opt.value)" />
                    <DimensionThumbnail
                      v-if="opt.url"
                      size="sm"
                      :image="{ url: opt.url, mediaType: opt.media_type === 'video' ? 'video' : 'image' }"
                      :label="opt.label ?? opt.value" />
                    <span
                      class="truncate"
                      :class="isSelected(dim.value, opt.value) ? 'text-foreground' : 'text-muted-foreground'">
                      {{ opt.label ?? opt.value }}
                    </span>
                  </label>

                  <p
                    v-if="getFilteredOptions(dim.value).length === 0"
                    class="text-xs text-muted-foreground px-1 py-0.5">
                    No results
                  </p>
                </div>
              </div>
            </div>
          </template>
        </draggable>
      </div>

      <div class="border-t" />

      <div class="flex flex-col gap-0.5">
        <p class="text-sm font-medium mb-1">Column dimensions</p>

        <draggable
          v-model="columnDimensions"
          item-key="value"
          handle=".drag-handle"
          ghost-class="opacity-40"
          class="flex flex-col gap-0">
          <template #item="{ element: dim }">
            <div class="flex items-center gap-1.5 rounded px-1 py-1 hover:bg-muted/50">
              <GripVertical
                class="drag-handle size-3.5 text-muted-foreground/40 shrink-0 cursor-grab active:cursor-grabbing" />
              <input
                :id="`col-dim-${dim.value}`"
                :checked="dim.checked"
                type="checkbox"
                class="accent-primary"
                @change="onColumnChecked(dim, $event)" />
              <label
                :for="`col-dim-${dim.value}`"
                class="flex-1 text-sm cursor-pointer select-none"
                :class="dim.checked ? 'text-foreground' : 'text-muted-foreground'">
                {{ dimensionLabel(dim.value) }}
              </label>
            </div>
          </template>
        </draggable>
      </div>
    </div>
  </div>
</template>
